import React from 'react'
import { CircularProgress, Alert } from '@mui/material'
import { snakeToCamel } from '../AdminDashboard/AdminEditRelease'
import { getUser } from '../../api/user'
import { getUserReleases } from '../../api/releases'
import { useQuery } from '@tanstack/react-query'

// Import the component parts
import DashboardHeader from './DashboardHeader'
import FeaturedReleaseCard from './FeaturedReleaseCard'
import CurrentReleasesSection from './CurrentReleasesSection'
import CurrentStreamCard from './CurrentStreamCard'
import LatestUpdatesSection from './LatestUpdatesSection'

const Panel = () => {
  const uid = localStorage.getItem('uid')
  
  const { 
    data: userData,
    isLoading: userLoading,
    error: userError
  } = useQuery({
    queryKey: ['user', uid],
    queryFn: () => getUser(uid),
    select: (data) => {
      return {
        id: data.user_id,
        ...snakeToCamel(data),
        artists: snakeToCamel(data.artists),
        earnings: data.total_paid?.toFixed(0) || 0,
        balance: data.balance?.toFixed(0) || 0,
        artistCount: data.artists?.length || 0
      }
    }
  });

  // Fetch releases data for featured release
  const { 
    data: releasesData,
    isLoading: releasesLoading,
    error: releasesError
  } = useQuery({
    queryKey: ['featured-release', uid],
    queryFn: () => getUserReleases(uid),
    select: (data) => {
      const mappedReleases = data.data.releases.map(releaseIn => ({
        ...releaseIn,
        status: releaseIn.status.toLowerCase().charAt(0).toUpperCase() + releaseIn.status.toLowerCase().slice(1),
        originalDate: releaseIn.original_date,
        digitalDate: releaseIn.digital_date,
        primaryGenre: releaseIn.primary_genre,
      }))
      return {
        releases: mappedReleases.sort((a, b) => new Date(b.originalDate) - new Date(a.originalDate)),
      };
    }
  });



  const isLoading = userLoading || releasesLoading;
  const error = userError || releasesError;

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '100px 0' }}>
          <CircularProgress size={50} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <Alert severity="error" sx={{ margin: '20px 0' }}>
          {error.message || 'Failed to load dashboard data'}
        </Alert>
      </div>
    )
  }

  // Get the first release or use default
  const featuredRelease = releasesData?.releases?.[0] || {
    title: "No Releases",
    artist: "Upload your first release",
    status: "Pending",
    originalDate: new Date().toISOString(),
    artwork: "/api/placeholder/400/300"
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    }).replace(/\//g, '-')
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <DashboardHeader userData={userData} />

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="dashboard-row">
          {/* Featured Release */}
          <FeaturedReleaseCard 
            featuredRelease={featuredRelease}
            userData={userData}
            formatDate={formatDate}
            formatTime={formatTime}
          />

          {/* Current Releases */}
          <CurrentReleasesSection />
        </div>

        {/* Bottom Section */}
        <div className="dashboard-bottom">
          {/* Current Stream */}
          <CurrentStreamCard userData={userData} />

          {/* Latest Updates */}
          <LatestUpdatesSection />
        </div>
      </div>

      <style jsx>{`
        .dashboard-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .dashboard-main {
          margin-bottom: 30px;
        }

        .dashboard-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-bottom: 30px;
        }

        .dashboard-bottom {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }

        @media (max-width: 768px) {
          .dashboard-row,
          .dashboard-bottom {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

export default Panel